<template>
  <!-- Task Card Content -->
  <div 
    v-for="(attachment, index) in messageData.attachments" 
    :key="index" 
    :class="bubbleContentClass"
    :style="{ marginBottom: index < messageData.attachments.length - 1 ? '8px' : '0' }"
  >
    <div 
      class="task-card-content"
    >
      <!-- Task Header -->
      <div class="task-header">
        <div class="task-icon w-[48px] h-[48px]">
          <img src="@/assets/MyPatient/message_task.svg" alt="Task" class="w-[22px] h-[20px]" />
        </div>
        <div class="task-info">
          <div class="task-title">
            Task: {{ getTaskData(attachment).title || 'Task' }}
          </div>
          <div class="task-meta">
            <div v-if="getFormattedDueDate(attachment)!=='N/A'" class="task-due-date" >Due by: {{ getFormattedDueDate(attachment) }}</div>
            <div class="task-status-badge" :class="getTaskData(attachment).status === 'true' ? 'status-completed' : 'status-unfinished'">
              <span class="status-text">{{ getTaskData(attachment).status === 'true' ? 'Completed' : 'Unfinished' }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Task Description & Actions -->
      <div class="task-content-actions">
        <div class="task-description">
          <span v-if="getTaskData(attachment).category === 'form_completion'">This is a form completion task for patient. You can view the form <a class="underline" :href="getSafeUrl(getTaskData(attachment).description)" target="_blank" rel="noopener noreferrer">here</a></span>
          <span v-else>{{ getTaskData(attachment).description }}</span>
        </div>
        <div class="task-actions">
        <button class="send-again-btn" @click="seeDetail(attachment)">
          <img
              src="@/assets/MyPatient/detail_icon.svg"
              alt="Download"
              class="w-[18px] h-[18px]"
          />
          <span>See Details</span>
        </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageCardTask',
  props: {
    messageData: {
      type: Object,
      required: true
    },
    isSent: {
      type: Boolean,
      default: false
    },
    rc_user_id: {
      type: String,
      required: true
    },
  },
  computed: {
    isBotMessage() {
      return this.messageData.u._id === 'oafKicny6TymdzNxQ' || 
             this.messageData.u._id === 'EuMKAsjDZTsLc74Kw';
    },
    
    bubbleContentClass() {
      if (this.isBotMessage) {
        return 'bubble-content not-user-bubble task-card-bubble';
      }
      return 'bubble-content task-card-bubble';
    }
  },
  
  methods: {
    getTaskData(attachment) {
      // The attachment object contains addon_column with task details
      const addonColumn = attachment?.addon_column;
      
      if (addonColumn) {
        // New format with addon_column
        return {
          title: addonColumn.title,
          category: addonColumn.category,
          dueDate: addonColumn.due_date,
          status: String(addonColumn.is_completed),
          description: addonColumn.description
        };
      }
      
      // Fallback to old format with fields array
      const fields = attachment?.fields || [];
      const getFieldValue = (title) => fields.find(field => field.title === title)?.value;
      
      return {
        title: getFieldValue('title'),
        category: getFieldValue('category'),
        dueDate: getFieldValue('due_date'),
        status: getFieldValue('is_completed'),
        description: getFieldValue('description')
      };
    },
    
    getFormattedDueDate(attachment) {
      const taskData = this.getTaskData(attachment);
      if (!taskData.dueDate) return 'N/A';
      
      // Convert UTC date string to local time and format as MM-DD-YYYY
      const date = new Date(taskData.dueDate);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}-${day}-${year}`;
    },
    
    getSafeUrl(url) {
      // Validate URL to prevent javascript: or other dangerous schemes
      if (!url) return '#';
      
      const urlStr = String(url).trim().toLowerCase();
      
      // Block dangerous URL schemes
      if (urlStr.startsWith('javascript:') || 
          urlStr.startsWith('data:') || 
          urlStr.startsWith('vbscript:')) {
        console.warn('Blocked potentially dangerous URL scheme:', url);
        return '#';
      }
      
      return url;
    },
    
    seeDetail(attachment) {
      const addonColumn = attachment?.addon_column;
      if (!addonColumn || !addonColumn.task_id) {
        console.warn('No task data found in attachment');
        return;
      }

      // Format task data to match the expected structure for handleEditTask
      const taskData = {
        task_id: addonColumn.task_id,
        title: addonColumn.title,
        description: addonColumn.description,
        due_date: addonColumn.due_date,
        start_date: addonColumn.start_date,
        end_date: addonColumn.end_date,
        is_completed: addonColumn.is_completed,
        is_active: addonColumn.is_active,
        category: addonColumn.category,
        priority: addonColumn.priority,
        rrule: addonColumn.rrule,
        calendar_id: addonColumn.assignee_calendar_id,
        created_by: addonColumn.assigner_id,
        // Extract patient_ids from assignee if it's a patient role
        patient_ids: addonColumn.role === 'patient' ? [addonColumn.assignee_calendar_id] : []
      };

      // Emit event to parent component
      this.$emit('edit-task', taskData);
    }
  }
}
</script>

<style scoped>
/* Task Card Specific Styles */
.task-card-bubble {
  background-color: #f9fbfd;
  border-radius: 20px;
  padding: 12px 24px;
  width: 100%;
  min-width: 480px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.task-card-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.task-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.task-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f2;
  border-radius: 8px;
}

.task-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-title {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #374957;
  letter-spacing: 0.1px;
}

.task-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.task-due-date {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.task-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 20px;
  align-self: flex-start;
}

.status-text {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.25px;
}
.status-completed {
  color: #25B8C2;
  background-color: #ebfff8;
}

.status-unfinished {
  color: #d10000;
  background-color: #faeaea;
}

.task-content-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
}

.task-description {
  width: 100%;
  padding: 8px 0 8px 56px;
  border-bottom: 1px dashed var(--DividerGray, #c4c4c4);
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: var(--TrueBlack, #333);
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 4px 0;
  width: 100%;
}

.send-again-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #1e4670;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-position: from-font;
}

.send-again-btn:hover {
  opacity: 0.8;
}

.send-again-btn span {
  white-space: nowrap;
}

/* Bubble content base styles */
.bubble-content {
  padding: 12px 24px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  position: relative;
  color: #000000;
  border: 1px solid var(--DividerGray, #C4C4C4);
}

.not-user-bubble {
  background-color: #f9fbfd;
  color: #333333;
  border-radius: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .task-card-bubble {
    padding: 12px 16px;
  }
  
  .task-description {
    padding-left: 0;
  }
}
</style>
